import { zodResolver } from '@hookform/resolvers/zod'
import type { PlanMenu } from '@monorepo/shared/schemas/common'
import {
  ActionTypeSchema,
  BILLING_PERIOD,
  BillingPeriodSchema,
  ENTITY_STATUS,
  EntityStatusSchema,
  PLAN_MENU,
  PRICING_MODEL,
  PricingModelSchema,
} from '@monorepo/shared/schemas/common'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { agentService } from '@/features/agents'
import type { Agent } from '@/features/agents/types/agent.types'
import type { Plan } from '@/features/plans/types/plan.types'
import type { Entitlement } from '@/shared/components/common/EntitlementsTable'
import { PageTitle } from '@/shared/components/common/PageTitle'
import { EntitlementsManagement } from '@/shared/components/plans/entitlements'
import { toast } from '@/shared/lib/toast'
import {
  usePlan,
  usePlanCharges,
  usePlanFeatures,
  useUpdatePlan,
} from '../../hooks/usePlans'
import { PlanDrawer } from '../PlanDrawer'
import { PlanConfigurationTabs } from './PlanConfigurationTabs'
import { PlanPreviewCard } from './PlanPreviewCard'

const fixedChargeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  description: z.string().optional(),
})

const recurringChargeSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  billingPeriod: BillingPeriodSchema,
  description: z.string().optional(),
})

const tierSchema = z.object({
  firstUnit: z.number().min(1, 'First unit must be at least 1'),
  lastUnit: z.number().min(1, 'Last unit must be at least 1'),
  perUnitPrice: z.number().min(0, 'Per unit price must be positive'),
  flatFee: z.number().min(0, 'Flat fee must be positive'),
})

const agentTierSchema = z.object({
  minUnits: z.number().min(0, 'Min units must be at least 0'),
  maxUnits: z.number().min(0, 'Max units must be positive').nullable(),
  unitPrice: z.string().min(1, 'Unit price is required'),
})

const signalPricingSchema = z.object({
  signalId: z.string().min(1, 'Signal is required'),
  signalName: z.string().min(1, 'Signal name is required'),
  signalKind: ActionTypeSchema,
  billingPeriod: BillingPeriodSchema,
  pricingModel: PricingModelSchema,
  unitPrice: z.number().min(0, 'Unit price must be positive'),
  includedUnits: z.number().min(0).optional(),
  minimumCommitmentUnits: z.number().min(0).optional(),
  tiers: z.array(agentTierSchema).optional(),
  isEnabled: z.boolean(),
})

const agentPricingSchema = z.object({
  agentId: z.string().min(1, 'Agent is required'),
  agentName: z.string().min(1, 'Agent name is required'),
  isCollapsed: z.boolean(),
  signalPricing: z.array(signalPricingSchema),
  description: z.string().optional(),
})

const planFeatureSchema = z
  .object({
    feature_id: z.string().min(1, 'Feature is required'),
    feature_name: z.string().optional(),
    billing_period: BillingPeriodSchema,
    pricing_model: PricingModelSchema,
    unit_price: z.string().min(0, 'Unit price must be positive'),
    included_units: z.number().min(0).optional(),
    minimum_commitment_units: z.number().min(0).optional(),
    tiers: z.array(tierSchema).optional(),
    description: z.string().optional(),
  })
  .refine(
    data => {
      if (
        data.pricing_model === PRICING_MODEL.tiered ||
        data.pricing_model === PRICING_MODEL.volume
      ) {
        return data.tiers && data.tiers.length > 0
      }
      return true
    },
    {
      message: 'Tiers are required for tiered and volume pricing models',
      path: ['tiers'],
    }
  )

const featurePricingSchema = z.object({
  featureId: z.string().min(1, 'Feature is required'),
  featureName: z.string().min(1, 'Feature name is required'),
  billingPeriod: BillingPeriodSchema,
  pricingModel: PricingModelSchema,
  unitPrice: z.number().min(0, 'Unit price must be positive'),
  includedUnits: z.number().min(0).optional(),
  minimumCommitmentUnits: z.number().min(0).optional(),
  tiers: z.array(tierSchema).optional(),
  description: z.string().optional(),
})

const planConfigSchema = z.object({
  name: z.string().min(1, 'Plan name is required'),
  description: z.string().optional(),
  status: EntityStatusSchema,
  currency: z.string().min(1, 'Currency is required'),
  billingPeriod: BillingPeriodSchema,
  platformFee: z.number().min(0).optional(),
  platformFeeBillingPeriod: BillingPeriodSchema.optional(),
  fixedCharges: z.array(fixedChargeSchema),
  recurringCharges: z.array(recurringChargeSchema),
  agentPricing: z.array(agentPricingSchema),
  featurePricing: z.array(featurePricingSchema),
  planFeatures: z.array(planFeatureSchema),
})

type PlanConfigData = z.infer<typeof planConfigSchema>

interface PlanDetailProps {
  planId: string
}

export function PlanDetail({ planId }: PlanDetailProps) {
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([])
  const [activeMenu, setActiveMenu] = useState<PlanMenu>(PLAN_MENU.pricing)
  const [entitlements, setEntitlements] = useState<Entitlement[]>([])
  const [editingEntitlement, setEditingEntitlement] =
    useState<Entitlement | null>(null)

  const queryClient = useQueryClient()

  // Fetch plan details, charges, and features
  const { data: planData } = usePlan(planId)
  const { data: chargesData } = usePlanCharges(planId)
  const { data: planFeaturesData } = usePlanFeatures(planId)
  const updatePlan = useUpdatePlan()

  const form = useForm<PlanConfigData>({
    resolver: zodResolver(planConfigSchema),
    defaultValues: {
      name: 'Basic Plan',
      description: '',
      status: ENTITY_STATUS.draft,
      currency: 'USD',
      billingPeriod: BILLING_PERIOD.monthly,
      platformFee: 0,
      platformFeeBillingPeriod: BILLING_PERIOD.monthly,
      fixedCharges: [],
      recurringCharges: [],
      agentPricing: [],
      featurePricing: [],
      planFeatures: [],
    },
  })

  // Update form when data is loaded
  // Note: 'form' is intentionally NOT in dependencies - React Hook Form best practice
  // The form object reference changes on every render, but form.reset() is stable
  React.useEffect(() => {
    if (planData && chargesData) {
      const { oneTimeCharges, recurringCharges } = chargesData

      form.reset({
        name: planData.name || 'Basic Plan',
        description: planData.description || '',
        status: planData.status || ENTITY_STATUS.draft,
        currency: planData.currency || 'USD',
        billingPeriod: planData.billing_period || BILLING_PERIOD.monthly,
        platformFee: 0, // Platform fee not in base Plan schema
        platformFeeBillingPeriod: BILLING_PERIOD.monthly,
        fixedCharges: oneTimeCharges.map(charge => ({
          name: charge.charge_name,
          amount: charge.amount,
          description: charge.description || '',
        })),
        recurringCharges: recurringCharges.map(charge => ({
          name: charge.charge_name,
          amount: charge.amount,
          billingPeriod: charge.billing_frequency || BILLING_PERIOD.monthly,
          description: charge.description || '',
        })),
        agentPricing: [],
        featurePricing: [],
        planFeatures:
          planFeaturesData?.map(feature => ({
            feature_id: feature.feature_id,
            feature_name: feature.feature?.feature_name || '',
            billing_period: BILLING_PERIOD.monthly, // Default value - not in PlanFeature schema
            pricing_model: feature.pricing_model || PRICING_MODEL.flat,
            unit_price: (feature.unit_price || 0).toString(),
            included_units: feature.included_units || 0,
            minimum_commitment_units: feature.minimum_commitment || 0,
            tiers: [],
            description: '',
          })) || [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planData, chargesData, planFeaturesData, form.reset])

  // Watch only specific fields needed by PlanPreviewCard to avoid unnecessary re-renders
  // Using individual watch() calls instead of watch() which creates new object on every render
  const name = form.watch('name')
  const status = form.watch('status')
  const currency = form.watch('currency')

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const agentsData = await agentService.getAgents({
          status: ENTITY_STATUS.active,
        })
        setAvailableAgents(agentsData.data || [])
      } catch {
        setAvailableAgents([])
      }
    }
    loadAgents()

    // Load dummy entitlements data
    const dummyEntitlements: Entitlement[] = [
      {
        id: 'ent_1',
        type: 'feature',
        name: 'Upload',
        description: 'File upload functionality',
        value: 'infinity',
        usageReset: 'monthly',
        isHardLimit: false,
      },
      {
        id: 'ent_2',
        type: 'feature',
        name: 'Download',
        description: 'File download functionality',
        value: 10,
        usageReset: 'monthly',
        isHardLimit: true,
      },
    ]
    setEntitlements(dummyEntitlements)
  }, [])

  const handleToggleStatus = async () => {
    const currentStatus = form.watch('status')
    const newStatus =
      currentStatus === ENTITY_STATUS.draft
        ? ENTITY_STATUS.active
        : currentStatus === ENTITY_STATUS.active
          ? ENTITY_STATUS.archived
          : ENTITY_STATUS.active

    try {
      await updatePlan.mutateAsync({
        id: planId,
        data: { status: newStatus },
      })
      form.setValue('status', newStatus)
      const statusText =
        newStatus === ENTITY_STATUS.active
          ? 'activated'
          : newStatus === ENTITY_STATUS.archived
            ? 'archived'
            : 'reactivated'
      toast.success(`Plan ${statusText} successfully`)
    } catch (_error) {
      toast.error('Failed to update plan status')
    }
  }

  // handlePlanUpdate removed - React Query automatically handles cache invalidation after mutations

  const _onSubmit = async (data: PlanConfigData) => {
    try {
      const _configData = {
        fixedCharges:
          data.fixedCharges?.map(charge => ({
            name: charge.name,
            amount: charge.amount,
            description: charge.description,
          })) || [],
        recurringCharges:
          data.recurringCharges?.map(charge => ({
            name: charge.name,
            amount: charge.amount,
            billingPeriod: charge.billingPeriod,
            description: charge.description,
          })) || [],
        agents:
          data.agentPricing
            ?.map(agentConfig => ({
              agentId: agentConfig.agentId,
              signalConfigurations: agentConfig.signalPricing
                .filter((sp: { isEnabled: boolean }) => sp.isEnabled)
                .map(
                  (signal: {
                    signalId: string
                    signalName: string
                    billingPeriod: string
                    pricingModel: string
                    unitPrice: number
                    minimumCommitmentUnits?: number
                    includedUnits?: number
                    signalKind: string
                    tiers?: unknown[]
                  }) => ({
                    signal_id: signal.signalId,
                    signal_name: signal.signalName,
                    billing_period: signal.billingPeriod,
                    pricing_model: signal.pricingModel,
                    unit_price: signal.unitPrice.toString(),
                    minimum_commitment_units:
                      signal.minimumCommitmentUnits || 0,
                    included_units: signal.includedUnits || 0,
                    tiers:
                      signal.pricingModel === PRICING_MODEL.tiered ||
                      signal.pricingModel === PRICING_MODEL.volume
                        ? (signal.tiers || [])
                            .filter(
                              (
                                tier
                              ): tier is {
                                minUnits?: number
                                min_units?: number
                                maxUnits?: number
                                max_units?: number
                                unitPrice?: string
                                unit_price?: string
                              } => Boolean(tier && typeof tier === 'object')
                            )
                            .map(tier => ({
                              minUnits: tier.minUnits || tier.min_units || 0,
                              maxUnits:
                                typeof (tier.maxUnits || tier.max_units) ===
                                'number'
                                  ? tier.maxUnits || tier.max_units
                                  : 999999999, // Use a very high number instead of null for infinity
                              unitPrice: (
                                tier.unitPrice ||
                                tier.unit_price ||
                                '0'
                              ).toString(),
                            }))
                        : [],
                    metadata: { kind: signal.signalKind },
                  })
                ),
            }))
            .filter(agent => agent.signalConfigurations.length > 0) || [],
        planFeatures:
          data.planFeatures?.map(feature => ({
            feature_id: feature.feature_id,
            billing_period: feature.billing_period,
            pricing_model: feature.pricing_model,
            unit_price: feature.unit_price,
            included_units: feature.included_units || 0,
            minimum_commitment_units: feature.minimum_commitment_units || 0,
            tiers:
              feature.pricing_model === PRICING_MODEL.tiered ||
              feature.pricing_model === PRICING_MODEL.volume
                ? (feature.tiers || []).map(tier => ({
                    firstUnit: tier.firstUnit,
                    lastUnit: tier.lastUnit || 999999999,
                    perUnitPrice: tier.perUnitPrice || 0,
                    flatFee: tier.flatFee || 0,
                  }))
                : undefined,
            description: feature.description,
          })) || [],
      }

      // TODO: Implement bulk plan configuration update
      // The planService.updatePlanConfiguration method doesn't exist yet
      // For now, we need to update charges and features separately using:
      // - planService.addCharge / updateCharge / removeCharge
      // - planService.addFeature / updateFeature / removeFeature
      // Invalidate cache to ensure fresh data is fetched
      queryClient.invalidateQueries({ queryKey: ['plans', planId] })
      queryClient.invalidateQueries({ queryKey: ['plans', planId, 'charges'] })
      queryClient.invalidateQueries({ queryKey: ['plans', planId, 'features'] })
      queryClient.invalidateQueries({ queryKey: ['plans'] })

      toast.success(
        'Plan configuration changes prepared (bulk update not yet implemented)'
      )
    } catch (_error) {
      toast.error('Failed to save plan configuration. Please try again.')
    }
  }

  return (
    <div className='space-y-6 container mx-auto py-6'>
      {/* Header */}
      <div className='p-6'>
        <PageTitle
          title='Plan Configuration'
          subtitle='Configure pricing and billing for your plan'
        />
      </div>

      {/* Two Column Layout */}
      <div className='grid grid-cols-1 lg:grid-cols-4 gap-8 px-6'>
        {/* Left Column - Plan Preview Card */}
        <div className='lg:col-span-1'>
          <div className='sticky top-6'>
            <PlanPreviewCard
              name={name}
              planId={planId}
              status={status}
              currency={currency}
              billing_period={planData?.billing_period}
              target_audience={planData?.target_audience}
              activeMenu={activeMenu}
              onEdit={() => setIsEditDrawerOpen(true)}
              onToggleStatus={handleToggleStatus}
              onActiveMenuChange={setActiveMenu}
            />
          </div>
        </div>

        {/* Right Side - Configuration Forms */}
        <div className='lg:col-span-3 space-y-8'>
          {activeMenu === PLAN_MENU.pricing ? (
            <PlanConfigurationTabs planId={planId} />
          ) : (
            // Entitlements Management
            <EntitlementsManagement
              entitlements={entitlements}
              setEntitlements={setEntitlements}
              availableAgents={availableAgents}
              editingEntitlement={editingEntitlement}
              setEditingEntitlement={setEditingEntitlement}
            />
          )}
        </div>
      </div>

      {/* Plan Edit Drawer */}
      <PlanDrawer
        plan={planData as Plan}
        open={isEditDrawerOpen}
        onOpenChange={setIsEditDrawerOpen}
      />
    </div>
  )
}
